<template>
  <!-- One layout: switching layouts remounts the page. A prop re-renders it in place. -->
  <NuxtLayout :focus-page="shownRoute().meta.focusPage" name="layout-default">
    <NuxtPage />
  </NuxtLayout>
</template>

<script setup lang="ts">
import nunitoSansVariable from "@fontsource-variable/nunito-sans/files/nunito-sans-latin-wght-normal.woff2";
import { useHead } from "#app";
import { useRoute, useRouter } from "#app/composables/router";
import { useNuxtApp } from "#app/nuxt";

import {
  useBaseMeta,
  useStructuredData,
} from "~/shared/composables/useMetaData.ts";
import { useOrganizationSchema } from "~/shared/seo/organizationSchema.ts";

const nuxtApp = useNuxtApp();
const route = useRoute();
const router = useRouter();

// The page on screen picks the variant, not the URL: useRoute() syncs once NuxtPage shows the
// next page. While hydrating it still holds the router's first navigation, which matched nothing
// (04.router.ts adds the routes after it) → read the route the server rendered.
function shownRoute() {
  return nuxtApp.isHydrating ? router.currentRoute.value : route;
}

function preloadFont(href: string) {
  return {
    rel: "preload",
    href,
    crossorigin: "",
    as: "font",
    type: "font/woff2",
    fetchpriority: "high",
  } as const;
}

useHead({
  link: [preloadFont(nunitoSansVariable)],
});

useBaseMeta();

useStructuredData(useOrganizationSchema());

useHead({
  link: [
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/shared/img/favicon/apple-touch-icon-lightmode.png",
      media: "(prefers-color-scheme: light)",
    },
    {
      rel: "icon",
      sizes: "32x32",
      href: "/shared/img/favicon/favicon-32x32-lightmode.png",
      media: "(prefers-color-scheme: light)",
    },
    {
      rel: "icon",
      sizes: "16x16",
      href: "/shared/img/favicon/favicon-16x16-lightmode.png",
      media: "(prefers-color-scheme: light)",
    },
    {
      rel: "shortcut icon",
      href: "/shared/img/favicon/favicon-lightmode.ico",
      media: "(prefers-color-scheme: light)",
    },

    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/shared/img/favicon/apple-touch-icon-darkmode.png",
      media: "(prefers-color-scheme: dark)",
    },
    {
      rel: "icon",
      sizes: "32x32",
      href: "/shared/img/favicon/favicon-32x32-darkmode.png",
      media: "(prefers-color-scheme: dark)",
    },
    {
      rel: "icon",
      sizes: "16x16",
      href: "/shared/img/favicon/favicon-16x16-darkmode.png",
      media: "(prefers-color-scheme: dark)",
    },
    {
      rel: "shortcut icon",
      href: "/shared/img/favicon/favicon-darkmode.ico",
      media: "(prefers-color-scheme: dark)",
    },

    { rel: "manifest", href: "/shared/img/favicon/manifest.json" },
  ],
  meta: [
    { name: "theme-color", content: "#ffffff" },
    { name: "msapplication-TileColor", content: "#ffffff" },
    {
      name: "msapplication-config",
      content: "/shared/img/favicon/browserconfig.xml",
    },
  ],
});
</script>
